import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { EgressCalculationResult } from "./spotHelpers";

type Props = {
  open: boolean;
  onClose: () => void;
  result: EgressCalculationResult | null;
};

export const EgressCalculationDialog: React.FC<Props> = ({
  open,
  onClose,
  result,
}) => {
  if (!result) {
    return null;
  }

  const {
    logs,
    totalSpots,
    spotsWithEgress,
    spotsWithoutEgress,
    totalBlockingRelationships,
  } = result;

  // Sort logs alphabetically by spot name
  const sortedLogs = [...logs].sort((a, b) =>
    a.spotName.localeCompare(b.spotName)
  );

  // Calculate blocked-by counts for each spot
  const blockedByMap = new Map<string, number>();
  sortedLogs.forEach((log) => {
    blockedByMap.set(log.spotId, 0);
  });
  sortedLogs.forEach((log) => {
    log.blocksSpots.forEach((blockedSpotId) => {
      blockedByMap.set(
        blockedSpotId,
        (blockedByMap.get(blockedSpotId) || 0) + 1
      );
    });
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Egress Blocking Calculation Results</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Summary Statistics */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Summary
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={`${totalSpots} Total Spots`} color="primary" />
              <Chip
                label={`${spotsWithEgress} With Egress Lane`}
                color="success"
              />
              <Chip
                label={`${spotsWithoutEgress} Without Egress Lane`}
                color="default"
              />
              <Chip
                label={`${totalBlockingRelationships} Blocking Relationships`}
                color="warning"
              />
            </Stack>
          </Box>

          {/* Results Table */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Spot Analysis
            </Typography>
            <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Spot</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Egress Lane
                      <Tooltip title="Shows available egress lanes with format: Lane Name (# spots blocked). Spots with multiple lanes will choose the lane with fewest blocks at departure time.">
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Has Lane?
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Blocking #
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Blocked By #
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLogs.map((log) => {
                  const blockedByCount = blockedByMap.get(log.spotId) || 0;
                  const hasConflict =
                    log.blocksSpots.length > 0 || blockedByCount > 0;

                  // Find which spots block this spot
                  const blockedBySpots = sortedLogs
                    .filter((l) => l.blocksSpots.includes(log.spotId))
                    .map((l) => l.spotName);

                  return (
                    <TableRow
                      key={log.spotId}
                      sx={{
                        backgroundColor: log.skipped
                          ? "action.disabledBackground"
                          : hasConflict
                          ? "warning.lighter"
                          : "success.lighter",
                      }}
                    >
                      <TableCell>{log.spotName}</TableCell>
                      <TableCell>{log.egressLane || "-"}</TableCell>
                      <TableCell align="center">
                        {log.hasEgressLane ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="disabled" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {log.blocksSpots.length > 0 ? (
                          <Tooltip
                            title={`Blocks: ${log.blocksSpots
                              .map((id) => {
                                const spot = sortedLogs.find(
                                  (l) => l.spotId === id
                                );
                                return spot?.spotName || id.slice(0, 8);
                              })
                              .join(", ")}`}
                          >
                            <Chip
                              label={log.blocksSpots.length}
                              size="small"
                              color="warning"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            0
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {blockedByCount > 0 ? (
                          <Tooltip
                            title={`Blocked by: ${blockedBySpots.join(", ")}`}
                          >
                            <Chip
                              label={blockedByCount}
                              size="small"
                              color="error"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            0
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color={
                            log.skipped ? "text.secondary" : "text.primary"
                          }
                        >
                          {log.reason}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Detailed Blocking Relationships */}
          {totalBlockingRelationships > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Details
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1, fontStyle: "italic" }}
              >
                Note: Gap spots are not blocked by their required spots since
                they sit in the gap between them
              </Typography>
              <Stack spacing={0.5}>
                {sortedLogs
                  .filter((log) => log.blocksSpots.length > 0)
                  .map((log) => {
                    const blockedNames = log.blocksSpots
                      .map((id) => {
                        const bl = sortedLogs.find((l) => l.spotId === id);
                        return bl?.spotName || id.slice(0, 8);
                      })
                      .join(", ");

                    return (
                      <Typography variant="caption" key={log.spotId}>
                        <Box component="span" sx={{ fontWeight: "bold" }}>
                          {log.spotName} blocks:
                        </Box>{" "}
                        {blockedNames}
                      </Typography>
                    );
                  })}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
